/**
 * Sentry Session Replay — trauma-safe configuration.
 *
 * Recovery Trauma™ is a mental-health platform. Session replay is only
 * acceptable if EVERY piece of reflective, personal, or clinical content is
 * masked before it ever leaves the browser. Defaults are inverted from the
 * Sentry defaults: mask everything, then opt specific chrome back in.
 *
 * Wire into Sentry.init on the CLIENT only (never SSR/server):
 *
 *   import * as Sentry from "@sentry/react";
 *   import { sentryScrubbers } from "@/lib/sentry-scrubbers";
 *   import { sentryContext } from "@/lib/sentry-context";
 *   import { sentryReplayIntegration, sentryReplaySampleRates } from "@/lib/sentry-replay";
 *
 *   Sentry.init({
 *     dsn: import.meta.env.VITE_SENTRY_DSN,
 *     integrations: [sentryReplayIntegration()],
 *     ...sentryReplaySampleRates,
 *     ...sentryContext,
 *     ...sentryScrubbers,
 *   });
 *
 * To hard-block replay on a specific element, add `data-sentry-block` (removes
 * it from the recording entirely) or `data-sentry-mask` (replaces text with `*`).
 */

import { replayIntegration } from "@sentry/react";

/**
 * Route paths where session replay is disabled entirely — even sampled sessions
 * are dropped before rrweb starts. These pages routinely contain the most
 * sensitive reflection content.
 */
const REPLAY_BLOCKED_ROUTES = [
  "/journal",
  "/guided-journal",
  "/dreams",
  "/mood",
  "/check-in",
  "/art-board",
  "/community",
  "/crisis",
  "/assessments",
  "/personalise", // intake answers
  "/account",
  "/billing",
  "/admin", // matches /admin, /admin_.*
];

/** Should this page be excluded from replay entirely? */
export function isReplayBlockedRoute(pathname: string): boolean {
  return REPLAY_BLOCKED_ROUTES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}_`),
  );
}

/**
 * CSS selectors that are always masked (text → `*`) or blocked (rendered as a
 * grey box) inside a replay. Broad on purpose — we err toward over-masking.
 *
 * BLOCK removes the element from the recording; use for images/media a user
 * uploaded (avatars, art board uploads) and for the raw-input textareas of
 * journals, dreams, and community composers.
 *
 * MASK keeps layout/interaction visible but replaces text with `*`; use for
 * anything that displays saved reflections back to the user.
 */
const BLOCK_SELECTORS = [
  '[data-sentry-block]',
  '[data-private]',
  '[data-pii]',
  'textarea',                    // any freeform text input
  'input[type="password"]',
  'input[type="email"]',
  'input[type="tel"]',
  'img[src*="user-uploads"]',
  'img[src*="avatars"]',
  'img[src*="art-pieces"]',
  'audio',
  'video',
];

const MASK_SELECTORS = [
  '[data-sentry-mask]',
  '[data-mask]',
  // Content areas that render saved reflection back to the user.
  '.journal-entry, .journal-body, [data-journal-body]',
  '.mood-note, [data-mood-note]',
  '.dream-body, [data-dream-body]',
  '.community-post, .community-comment, [data-community-body]',
  '.affirmation-personal, [data-personal-affirmation]',
  '.assessment-answer, [data-assessment-answer]',
  '.crisis-note, [data-crisis-note]',
  '.contract-body, [data-contract-body]',
  // User identifiers that show up in the shell.
  '[data-user-email]',
  '[data-user-name]',
];

/**
 * Build the Replay integration with trauma-safe masking defaults.
 * Mask everything by default; nothing is opted back in globally — surface
 * chrome (nav, buttons, headings without personal data) stays visible because
 * it doesn't contain reflection content, not because we unmasked it.
 */
export function sentryReplayIntegration() {
  return replayIntegration({
    // Mask everything by default.
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    // Never record network request/response bodies alongside replay.
    networkDetailAllowUrls: [],
    networkCaptureBodies: false,
    networkRequestHeaders: [],
    networkResponseHeaders: [],
    // Extra selector-level guarantees on top of the "mask all" defaults.
    mask: MASK_SELECTORS,
    block: BLOCK_SELECTORS,
    // Never record uploaded images/media even if the URL slips a filter.
    unblock: [], // do NOT unblock anything project-wide
    unmask: [],  // do NOT unmask anything project-wide
    // Trim URL params that carry tokens (matches sentry-scrubbers).
    beforeAddRecordingEvent: (event) => {
      // Drop the whole recording chunk if we're currently on a blocked route.
      if (
        typeof window !== "undefined" &&
        isReplayBlockedRoute(window.location.pathname)
      ) {
        return null;
      }
      return event;
    },
  });
}

/**
 * Sample rates: 10% of sessions overall, 100% of sessions with an error.
 * Errors in production are rare enough that we want every one — normal
 * sessions are sampled low to stay well under Sentry's replay quota.
 */
export const sentryReplaySampleRates = {
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
};
